import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogFooter
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NotificationRow } from "@/components/ui/notification-row";
import useMusioApi from "@/contexts/client/musio-client-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { Edit } from "lucide-react";
import { useRef, useState } from "react";

const ERROR_FAILED_TO_REQUEST_EMAIL = "There was an error when requesting an email change";
const CONFIRMATION_CHECK_EMAIL = "Please check your new email to continue";

export const EmailModal = () => {
  const { musioApi } = useMusioApi();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const emailRef = useRef<HTMLInputElement>(null);

  const { mutate, isPending, isError, isSuccess } = musioApi.useMutation("post", "/user/email");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const isEmailInvalid = email.trim() === "" || !/\S+@\S+\.\S+/.test(email.trim());
  const isDisabled = isPending || isEmailInvalid;
  const hasNotification = isError || isSuccess;
  const isMobile = useIsMobile();

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button className="p-2 px-5 bg-musio-mid-gray text-musio-light-lilac hover:text-musio-mid-gray">
          {isMobile ? <Edit /> : "Change Email"}
        </Button>
      </AlertDialogTrigger>

      <AlertDialogPortal>
        <AlertDialogOverlay className="AlertDialogOverlay" />
        <AlertDialogContent className="AlertDialogContent">
          <form
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault(); // dont reload page im doing this later
              mutate({ body: { email: email.trim() } })
            }}
            className="min-h-0 flex flex-col gap-4">
            <AlertDialogTitle className="AlertDialogTitle flex items-center justify-between gap-4 p-8 bg-musio-bg">
              <div className="text-[30px] font-semibold text-musio-light-lilac">
                Change Email
              </div>
              <AlertDialogCancel asChild>
                <Button
                  type="button"
                  className="select-none px-8 h-[38px] flex items-center justify-center rounded-full font-bold bg-musio-red text-musio-light-lilac cursor-pointer"
                >
                  <span className="text-sm">Go Back</span>
                </Button>
              </AlertDialogCancel>
            </AlertDialogTitle>

            <AlertDialogDescription className="AlertDialogDescription p-8 space-y-3 bg-musio-bg">
              {hasNotification ?
                <NotificationRow intent={isError ? "error" : "info"}>
                  {isError ? ERROR_FAILED_TO_REQUEST_EMAIL : CONFIRMATION_CHECK_EMAIL}
                </NotificationRow> : null
              }
              <Input
                ref={emailRef}
                type="email"
                id="email"
                placeholder="name@example.com"
                value={email}
                onChange={handleEmailChange}
                required
                autoComplete="email"
              />
            </AlertDialogDescription>

            <AlertDialogFooter className="p-8 flex items-center justify-center bg-musio-bg">
              {isSuccess == false ?
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className={`
                    select-none w-[404px] h-[35px] flex items-center justify-center
                    rounded-2xl font-bold text-musio-light-lilac cursor-pointer
                    bg-musio-green disabled:opacity-60
                  `}
                >
                  <p className="text-sm text-musio-mid-gray">
                    {isPending ? "Submitting…" : "Submit"}
                  </p>
                </Button>
                :
                <Button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    location.reload();
                  }}
                  className={`
                    select-none w-[404px] h-[35px] flex items-center justify-center
                    rounded-2xl font-bold text-musio-light-lilac cursor-pointer
                    bg-musio-green disabled:opacity-60
                  `}
                >
                  <p className="text-sm text-musio-mid-gray">
                    Close
                  </p>
                </Button>
              }
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};
